import {constants} from './constants.js';
import * as helpers from './helpers.js';
import * as i18n from './i18n.js';
import * as server from './server.js';

const stateStrings = {
  countryIso: 'sal-country-iso',
  languageIso: 'sal-language-iso',
  vatState: 'sal-vat-state',
  sessionState: 'sal-session',
  cartState: 'sal-cart-state',
};

let state = {
  isNewSession: false,
  languageIso: 'en',
  browserLanguageIso: null,
  currencyIso: 'USD',
  countryIso: 'US',
  vatState: null,
}

let languageBarLanguageIso = null;

async function init() {
  if (isNewSession() === false) {
    state.isNewSession = false;
    state.languageIso = i18n.getLanguageIso();
    state.browserLanguageIso = null;
    state.currencyIso = getCurrency();
    state.countryIso = getCountryIso();
    state.vatState = getVatState();

    // sets cookie, used as hint, but not authoritative, but i18n.getLanguageIso
    setLanguageIso(state.languageIso);

  } else {
    let countryIso, browserLanguageIso;
    [countryIso, browserLanguageIso] = await Promise.all(
        [server.getCountryIsoFromIp(), i18n.getBrowserLanguageIso()]);
    const languageIso = i18n.getLanguageIso();

    setCountryIso(countryIso);  // uppercase
    setLanguageIso(languageIso);

    state.isNewSession = true;
    state.languageIso = languageIso,
    state.browserLanguageIso = browserLanguageIso,
    state.currencyIso = getCurrency();  // based on country only
    state.countryIso = countryIso, state.vatState = getVatState();
  }
  stateLoaded();
}

function isNewSession() {
  const session = helpers.getCookie(stateStrings.sessionState);

  if (session !== null) {
    return false;
  }
  helpers.setCookie(
      stateStrings.sessionState, '', 30);  // also sets -localhost version
  return true;
}

function getState() {
  return Object.assign({}, state);
}

function verifyVatNumberAndSaveVatState(vatNumber) {
  return new Promise((resolve, reject) => {
    server.verifyVatNumber(vatNumber)
        .then((vatState) => {
          setVatState(vatState);
          resolve(vatState);
        })
        .catch((err) => {
          console.log(err);
          setVatState(null);
          reject();
        });
  });
}

function setVatState(vatState) {
  if (vatState === null) {
    vatState = {};
  }
  const vatStateStr = JSON.stringify(vatState);

  state.vatState = vatState;
  window.localStorage.setItem(stateStrings.vatState, vatStateStr);
}

function getVatState() {
  const vatStateStr = window.localStorage.getItem(stateStrings.vatState);

  if (vatStateStr === null)  // never been set
  {
    return null;
  }

  const vatState = JSON.parse(vatStateStr);

  if (vatState === null) return null;

  if (!vatState.hasOwnProperty('number') || !vatState.hasOwnProperty('company'))
    return null;

  return vatState;
}

function setCartState(state) {
  const stateStr = JSON.stringify(state);
  helpers.setCookie(stateStrings.cartState, stateStr);
}

// null if it doesn't exist
function getCartState() {
  const cartStateStr =
      helpers.getCookie(stateStrings.cartState);  // null if doesn't exist
  if (cartStateStr === null) return null;

  const cartState = JSON.parse(cartStateStr);
  return cartState;
}

function setCountryIso(countryIso) {
  countryIso = countryIso.toUpperCase();

  if (countryIso.length != 2)
    throw new Error(`country ${countryIso} is invalid`);

  state.countryIso = countryIso;
  helpers.setCookie(stateStrings.countryIso, countryIso);
}

function getCountryIso() {
  const countryIso = helpers.getCookie(
      stateStrings.countryIso);  // upper case; null if doesn't exist
  return countryIso;
}

function getCountryName() {
  const countryIso = getCountryIso();
  return constants.allCountries[countryIso];
}

function getCurrency() {
  const country_iso = getCountryIso();  // upper case
  for (const currency in constants.countriesByCurrency) {
    const countryArray = constants.countriesByCurrency[currency];

    if (countryArray.includes(country_iso)) {
      return currency;  // upper case
    }
  }
  return 'USD';  // USD is the "catch all".
}


function setLanguageIso(lang) {
  lang = lang.toLowerCase();

  state.languageIso = lang;
  helpers.setCookie(stateStrings.languageIso, lang);
}

function getLanguageIso() {
  const lang = helpers.getCookie(
      stateStrings.languageIso);  // lower; null of doesn't exist
  return lang;
}
function getLanguageIsoFromCookie() {
  // public function for i18n use
  return getLanguageIso();
}

function setBrowserLanguageIso(lang) {
  state.browserLanguageIso = lang;
}

function getBrowserLanguageIso() {
  return state.browserLanguageIso;
}


let stateLoadedCallbacks = [];
let stateHasLoaded = false;
function callOnStateLoaded(f) {
  if (stateHasLoaded) {
    f();
  } else {
    stateLoadedCallbacks.push(f);
  }
}

function stateLoaded() {
  stateHasLoaded = true;
  stateLoadedCallbacks.forEach(f => f());
}


// todo: refactor so no 'getters' are public except getState
export {
  init,
  getState,

  verifyVatNumberAndSaveVatState,
  setVatState,
  getVatState,

  setCartState,
  getCartState,

  setCountryIso,
  getCountryIso,
  getCountryName,

  getCurrency,

  setLanguageIso,
  getLanguageIso,
  getLanguageIsoFromCookie,

  setBrowserLanguageIso,
  getBrowserLanguageIso,

  callOnStateLoaded,
};
